import { useState, useEffect, useRef } from 'react'
import { getAccessToken, showToast } from './helpers'
import { confirm } from './helpers'

export const useSocketManager = () => {

  const serverURL = process.env.REACT_APP_WSS_BASE_URL
  const accessToken = getAccessToken()
  const HEARTBEAT_INTERVAL = 10000
  const PRESENCE_INTERVAL = 5000
  const attemptsRef = useRef(0)

  const [isSocketOpen, setIsSocketOpen] = useState(false)

  // Mirror of isSocketOpen for the timer callbacks. connect()/onOpen() are wired
  // into ws.current at mount and stay pinned to that render's closures forever
  // (reconnects re-use the same ones), so anything they schedule would read
  // isSocketOpen as permanently `false`. That silently killed the heartbeat: the
  // timer fired once, returned early, and never re-armed — so the server's 70s
  // no-heartbeat check (oh_chat/services/consumer.py check_heartbeat) closed
  // every socket at ~90s, for every user. Read the ref, never the state, from
  // anything a socket handler schedules.
  const isSocketOpenRef = useRef(false)

  const ws = useRef(null)
  const reconnectTimeout = useRef(null)
  const sendHeartBeatMessageTimer = useRef(null)
  const sendPresenceMessageTimer = useRef(null)
  const openEventListeners = useRef([])
  const messageListeners = useRef([])

  const connect = () => {
    ws.current = new WebSocket(
      `${serverURL}/?token=${accessToken}`
    )
    ws.current.onopen = onOpen
    ws.current.onclose = onClose
    ws.current.onmessage = onMessage
    ws.current.onerror = onError
  }

  const setSocketOpen = (value) => {
    isSocketOpenRef.current = value
    setIsSocketOpen(value)
  }

  const onOpen = () => {
    attemptsRef.current = 0;
    setSocketOpen(true)
    onOpenListener()
    // showToast('websocket connected', 'success')
    sendHeartBeatMessageTimer.current = window.setTimeout(
      sendHeartBeatMessage,
      HEARTBEAT_INTERVAL
    )
  }

  const onClose = (event) => {
    console.log('WS onClose called')
    setSocketOpen(false)
    clearTimers()
    removeSocketListeners()

    attemptsRef.current += 1

    // Keep retrying with a capped backoff. Previously this gave up after 3
    // attempts and only offered a dismissable "please refresh" dialog — and
    // dismissing it left ws.current pointing at a dead socket for the rest of
    // the page session, so every subsequent send silently vanished. Chat must
    // never be able to end up permanently offline without the user knowing.
    const delay = Math.min(2000 * attemptsRef.current, 30000)
    clearTimeout(reconnectTimeout.current)
    reconnectTimeout.current = window.setTimeout(connect, delay)

    if (attemptsRef.current === 3) {
      confirm(
        () => window.location.reload(),
        'There seems to have been a connectivity issue. Messages may not send until this is resolved.',
        () => {
          console.error('WS connectivity issue — retrying in the background')
        },
        {
          title: '',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Refresh now'
        }
      )
      console.error('WebSocket reconnection failed')
    }
  }

  const onMessage = (event) => {
    onMessageListener(event.data)
  }

  const onError = (event) => {
    console.error('Socket error', event)
  }

  const clearTimers = () => {
    clearTimeout(sendHeartBeatMessageTimer.current)
    clearTimeout(sendPresenceMessageTimer.current)
    console.log('WS clear timeouts called')
  }

  const clearPresenceTimer = () => {
    clearTimeout(sendPresenceMessageTimer.current)
    console.log('WS clear presence message timeout called')
  }

  const close = () => {
    console.log('WS closed')
    clearTimers()
    removeSocketListeners()
    if (ws.current) ws.current.close(1000, 'WS Deliberately closed')
    // showToast('websocket closed', 'info')
  }

  const reconnect = () => {
    close()
    connect()
  }

  useEffect(() => {
    console.log('is WS open', isSocketOpen)
  }, [isSocketOpen])


  const sendHeartBeatMessage = () => {
    if (!isSocketOpenRef.current) return

    const data = { heartbeat: true }
    sendDataOnSocket(data)
    sendHeartBeatMessageTimer.current = window.setTimeout(
      sendHeartBeatMessage,
      HEARTBEAT_INTERVAL
    )
  }

  const sendPresenceMessage = (uuid) => {
    if (!isSocketOpenRef.current) return

    const data = { presence: 'true', channel_id: uuid }

    if (uuid) {
      sendDataOnSocket(data)

      //to clear any previous timers
      if (sendPresenceMessageTimer.current) {
        clearTimeout(sendPresenceMessageTimer.current)
      }

      sendPresenceMessageTimer.current = window.setTimeout(
        () => sendPresenceMessage(uuid),
        PRESENCE_INTERVAL
      )
    }
    else {
      console.log('Unable to send presence. Channel uuid is undefined')
    }
  }

  // Returns whether the frame actually went out. Callers that render a message
  // optimistically MUST check this — a send into a CONNECTING/CLOSING/CLOSED
  // socket throws InvalidStateError, and swallowing that is how a message ends
  // up displayed as sent while never reaching the server.
  const sendDataOnSocket = (data) => {
    if (!ws.current) {
      console.log('Socket not created')
      return false
    }
    if (ws.current.readyState !== WebSocket.OPEN) {
      console.error('Socket not open, data not sent', ws.current.readyState)
      return false
    }
    try {
      ws.current.send(JSON.stringify(data))
      return true
    } catch (e) {
      console.error('Socket not open or error sending data', e)
      return false
    }
  }

  const addMessageListener = (listener) => {
    messageListeners.current.push(listener)
  }

  const removeMessageListener = (listener) => {
    messageListeners.current = messageListeners.current.filter(
      (item) => item !== listener
    )
  }

  const onMessageListener = (msg) => {
    messageListeners.current.forEach((listener) => listener(msg))
  }

  const addOpenListener = (listener) => {
    openEventListeners.current.push(listener)
  }

  const removeOpenListener = (listener) => {
    openEventListeners.current = openEventListeners.current.filter(
      (item) => item !== listener
    )
  }

  const onOpenListener = () => {
    openEventListeners.current.forEach((listener) => listener())
  }

  const removeSocketListeners = () => {
    if (ws.current) {
      ws.current.onopen = null
      ws.current.onclose = null
      ws.current.onmessage = null
      ws.current.onerror = null
    }
  }

  return {
    isSocketOpen,
    sendDataOnSocket,
    connect,
    close,
    addMessageListener,
    removeMessageListener,
    addOpenListener,
    removeOpenListener,
    sendPresenceMessage,
    clearPresenceTimer
  }
}
